// src/utils/instructorAuthBackfill.js
//
// Pass 50S item 1 — CANONICAL identity-link backfill. When an existing organizationMember gains
// (or changes) its linked Auth account, their already-assigned bookings must appear in "My
// Schedule" IMMEDIATELY — NOT on the next spreadsheet poll. This is the one reusable operation for
// that, independent of the sheet: it is meant to be called from the Auth-link / invite-accept /
// Team flow (Pass 50A) as well as from the sync.
//
// It updates ONLY `instructorAuthUserId` on bookings that already belong to the member
// (instructorId === member.userId, same org). It never touches the assignment (instructorId
// stays), never rewrites createdAt, never emits a scheduleChange or reassignment notification, and
// never creates or duplicates a booking. PURE planner + framework-free apply (caller injects the
// per-booking writer), so there are zero production writes from importing this module.
//
// WIRING STATUS (Pass 55 Stage D): AUTH_BACKFILL_WIRED. `onOrganizationMembershipActivated`
// (src/utils/organizationInviteHooks.js) now invokes this planner on activation: when a member gains a
// linked authUserId, its already-assigned bookings get instructorAuthUserId set (field-only, no
// reassignment / createdAt rewrite / notification), so the booking becomes visible in that member's My
// Schedule. The invite membership link preserves the canonical member.userId and sets authUserId only.
export const AUTH_BACKFILL_WIRING_STATUS = 'AUTH_BACKFILL_WIRED';
export const AUTH_BACKFILL_INTENDED_SEAM = 'onOrganizationMembershipActivated';

// Which of `bookings` need their delivery Auth link (re)pointed at this member's account.
// Returns { updates: [{ bookingId, set: { instructorAuthUserId } }] }.
export function planInstructorAuthBackfill(member = {}, bookings = []) {
  const targetAuth = String(member.authUserId || '');
  const orgId = member.organizationId;
  const userId = String(member.userId || '');
  if (!targetAuth || !orgId || !userId) {
    return { updates: [], reason: !targetAuth ? 'member_has_no_auth' : 'incomplete_member' };
  }
  const updates = [];
  for (const b of bookings) {
    if (b.organizationId !== orgId) continue;                 // scope to the member's org
    if (String(b.instructorId || '') !== userId) continue;    // only bookings already theirs
    if (String(b.instructorAuthUserId || '') === targetAuth) continue; // already correct → skip
    // missing OR stale delivery link → repoint ONLY this field
    updates.push({ bookingId: b.id, set: { instructorAuthUserId: targetAuth } });
  }
  return { updates };
}

// Apply a backfill plan. `applyOne(bookingId, patch)` MUST update only the given field(s) plus an
// updatedAt touch — it must NOT write createdAt, an assignment change, or any scheduleChange/
// notification. Kept framework-free so the invite/Team flow can supply a batched Firestore writer.
export async function applyInstructorAuthBackfill(plan = { updates: [] }, applyOne) {
  const applied = [];
  for (const u of plan.updates) {
    await applyOne(u.bookingId, u.set);
    applied.push(u.bookingId);
  }
  return { applied, count: applied.length };
}
